import http, { AxiosResponse } from '@libs/http';
import {
  Calendar,
  CreateOrderResponse,
  Orders,
  OrderPosition,
  Trades,
  OrdersHistoryQuery,
  FileReportResult,
  OfflineStatus,
  CreateOfflineOrderItem,
  OrderItem,
  SummarizedOrders,
  CreateConditionalOrderRequest,
  CreateSLTPOrderRequest,
  OMSStatus,
} from '@libs/api-reference';

import { NEXT_TSE_API } from './endpoints';
import { transformOrderHistory } from './utils/transform/orderHistory';

type TCreateOrderFormModel = {
  quantity: number;
  deviceType: number;
  orderExecutionType: string;
  price: number;
  orderSide: number;
  instrumentIdentification: string | null;
  popupSelectionMethod?: number;
  validityDate?: string | Date | null;
  investorBourseCodeId: number;
  validateAssetOnSell: boolean;
  disclosedQuantity?: number;
  minimumQuantity?: number;
  accountRouteType?: number;
  clientInternalId?: number;
  validityType?: string | number | null;
  orderType?: number;
  executedQuantity?: number;
  marginSource?: number | null;
  legType?: string | null;
};

type TCreateOfflineOrderFormModel = {
  instrumentIdentification: string;
  orderSide: number;
  quantity: number;
  price: number | null;
  orderExecutionType: string;
  validityDate: Date;
  clientTag: string;
  value: number;
};

type TDeleteOrderOptions = {
  orderId: string;
  personageId: number;
  orderExecutionType?: string;
  instrumentIdentification: string;
};

type TEditOrderOptions = Omit<TCreateOrderFormModel, 'orderEntryDeviceType'> & {
  parentId: string;
  accountRouteTypeId: number;
  remainingQuantity: number;
};

type TGetOrderPositionFormModel = {
  isin?: string | null;
  queryParams: {
    orderSide?: number;
    exchangeCode?: string | null;
    orderSequenceNumber?: number;
    entryDate?: string;
  };
};

type TDeleteOfflineOrderOptions = {
  algorithmId: string;
  action: number;
};

function getServerClock() {
  return http.get<Calendar>(`${NEXT_TSE_API}/calendars`, {
    schema: Calendar,
  });
}

function getOfflineStatus() {
  return http.get<OfflineStatus>(`${NEXT_TSE_API}/calendars/isoffline`, {
    schema: OfflineStatus,
  });
}

function getOMSStatus() {
  return http.get<OMSStatus>(`${NEXT_TSE_API}/order/users/migrated`, {
    schema: OMSStatus,
  });
}

function getOrderSummarize() {
  return http.get<SummarizedOrders>(`${NEXT_TSE_API}/trades/summarize`, {
    schema: SummarizedOrders,
  });
}

createOrder.url = `${NEXT_TSE_API}/orders` as const;
function createOrder(
  data: TCreateOrderFormModel
): Promise<AxiosResponse<CreateOrderResponse>> {
  return http.post<CreateOrderResponse>(createOrder.url, data, {
    schema: CreateOrderResponse,
  });
}

getOrders.url = `${NEXT_TSE_API}/orders` as const;
function getOrders(): Promise<AxiosResponse<Orders>> {
  return http.get<Orders>(getOrders.url, {
    schema: Orders,
  });
}

function getOrder(orderId: string) {
  return http.get<OrderItem>(`${NEXT_TSE_API}/orders/{orderId}`, {
    urlParams: { orderId },
    schema: OrderItem,
  });
}

function deleteOrder(data: TDeleteOrderOptions) {
  return http.post(`${NEXT_TSE_API}/orders/cancel`, data);
}

function deleteOfflineOrder(data: TDeleteOfflineOrderOptions) {
  return http.put(`${NEXT_TSE_API}/falconorders`, data);
}

function updateOrder(data: TEditOrderOptions) {
  return http.put(`${NEXT_TSE_API}/orders`, data);
}

function getOrderPosition(data: TGetOrderPositionFormModel) {
  return http.get<OrderPosition>(
    `${NEXT_TSE_API}/marketdepths/{isin}/orders/position`,
    {
      urlParams: { isin: data.isin },
      params: data.queryParams,
      schema: OrderPosition,
    }
  );
}

export function getNewMicroTransactions(orderId: string) {
  return http.get<Trades>(`${NEXT_TSE_API}/orders/{orderId}/trades`, {
    urlParams: { orderId },
    schema: Trades,
  });
}

export function getMicroTransactions(orderId: string) {
  return http.get<Trades>(`${NEXT_TSE_API}/trades`, {
    params: {
      Filters: JSON.stringify([
        { property: 'OrderId', value: orderId, operator: '=' },
      ]),
    },
    schema: Trades,
  });
}

export function getMicroTransactionsReport(
  params: Record<string, any>,
  queryParams: string | undefined
) {
  return http.get<FileReportResult>(
    `${NEXT_TSE_API}/trades/file${queryParams ? `?${queryParams}` : ''}`,
    {
      params,
      schema: FileReportResult,
    }
  );
}

getOrdersHistory.url = `${NEXT_TSE_API}/report/orders` as const;
export function getOrdersHistory(
  params: Record<string, any>,
  queryParams: string | undefined
): Promise<AxiosResponse<OrdersHistoryQuery>> {
  return http.get<OrdersHistoryQuery>(
    `${getOrdersHistory.url}${queryParams ? `?${queryParams}` : ''}`,
    {
      transformResponse: (data) => transformOrderHistory(JSON.parse(data)),
      params,
      schema: OrdersHistoryQuery,
    }
  );
}
export function getOrdersHistoryReport(
  params: Record<string, any>,
  queryParams: string | undefined
) {
  return http.get<FileReportResult>(
    `${NEXT_TSE_API}/report/orders/file${queryParams ? `?${queryParams}` : ''}`,
    {
      params,
      schema: FileReportResult,
    }
  );
}
function createBasketOrder(data: TCreateOrderFormModel) {
  return http.post(`${NEXT_TSE_API}/baskets`, data);
}

function createOfflineOrder(data: TCreateOfflineOrderFormModel) {
  return http.post<CreateOrderResponse>(`${NEXT_TSE_API}/falconorders`, data, {
    schema: CreateOfflineOrderItem,
  });
}

export function createConditionalOrder(data: CreateConditionalOrderRequest) {
  return http.post(`${NEXT_TSE_API}/conditionalorder/conditionalorders`, data);
}

function createSLTPOrder(data: CreateSLTPOrderRequest) {
  return http.post(`${NEXT_TSE_API}/falconorders/sltp`, data);
}

export {
  getServerClock,
  getOrders,
  createOrder,
  getOrderPosition,
  deleteOrder,
  updateOrder,
  createBasketOrder,
  createOfflineOrder,
  getOfflineStatus,
  deleteOfflineOrder,
  getOrder,
  getOrderSummarize,
  getOMSStatus,
  createSLTPOrder,
};
